import axios from 'axios'
import { platformEnv } from '@/platform/config/env'

const ACCESS_TOKEN = 'sso_access_token'
const REFRESH_TOKEN = 'sso_refresh_token'
const EXPIRES_AT = 'sso_token_expires_at'

function getCookie(name: string): string | null {
  const value = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=')
  return value ? decodeURIComponent(value) : null
}

function rootCookieDomain(): string | undefined {
  const host = window.location.hostname
  return host === 'hocnhe.com' || host.endsWith('.hocnhe.com') ? '.hocnhe.com' : undefined
}

function setCookie(name: string, value: string, maxAge?: number): void {
  const attributes = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax']
  if (window.location.protocol === 'https:') attributes.push('Secure')
  const domain = rootCookieDomain()
  if (domain) attributes.push(`Domain=${domain}`)
  if (maxAge !== undefined) attributes.push(`Max-Age=${maxAge}`)
  document.cookie = attributes.join('; ')
}

export function getAccessToken(): string | null {
  return getCookie(ACCESS_TOKEN)
}

export function getRefreshToken(): string | null {
  return getCookie(REFRESH_TOKEN)
}

export function getTokenExpiresAt(): string | null {
  return getCookie(EXPIRES_AT)
}

/**
 * Kiểm tra access token có sắp hết hạn không (dưới 60 giây hoặc đã hết hạn)
 */
export function isTokenExpiring(bufferSeconds = 60): boolean {
  const expiresAt = getTokenExpiresAt()
  if (!expiresAt) {
    // Nếu không có expires_at nhưng có token thì coi như sắp hết hạn để refresh
    return !getAccessToken()
  }

  const expireTime = new Date(expiresAt).getTime()
  if (Number.isNaN(expireTime)) return true

  const now = Date.now()
  return now + bufferSeconds * 1000 >= expireTime
}

export function clearSessionTokens(): void {
  for (const name of [ACCESS_TOKEN, REFRESH_TOKEN, EXPIRES_AT]) {
    setCookie(name, '', 0)
    // Clear cả scope không domain (local path) phòng trường hợp cookie cũ
    document.cookie = `${name}=; Path=/; Max-Age=0`
  }
}

function saveTokens(payload: { access_token: string; refresh_token: string; expires_in: number }): void {
  const expiryDate = new Date(Date.now() + payload.expires_in * 1000)
  setCookie(ACCESS_TOKEN, payload.access_token, payload.expires_in)
  setCookie(REFRESH_TOKEN, payload.refresh_token, 30 * 24 * 60 * 60)
  setCookie(EXPIRES_AT, expiryDate.toISOString(), payload.expires_in)
}

let activeRefreshPromise: Promise<string | null> | null = null

export async function refreshAccessToken(): Promise<string | null> {
  if (activeRefreshPromise) {
    return activeRefreshPromise
  }

  activeRefreshPromise = (async () => {
    const refreshToken = getCookie(REFRESH_TOKEN)
    if (!refreshToken) return null

    try {
      const response = await axios.post(
        `${platformEnv.ssoApiUrl}/v1/auth/refresh`,
        { refresh_token: refreshToken },
        { timeout: 10_000 }
      )
      const payload = response.data?.data
      if (!response.data?.success || !payload?.access_token || !payload?.refresh_token || !payload?.expires_in) {
        return null
      }
      saveTokens(payload)
      return payload.access_token
    } catch (cause) {
      if (axios.isAxiosError(cause) && [400, 401, 403].includes(cause.response?.status ?? 0)) {
        clearSessionTokens()
      }
      return null
    } finally {
      activeRefreshPromise = null
    }
  })()

  return activeRefreshPromise
}

/**
 * Trả về access token hợp lệ:
 * - Nếu token chưa hết hạn: trả về ngay.
 * - Nếu token sắp hết hạn hoặc không có token: chủ động gọi refresh token trước khi gửi request.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const current = getAccessToken()
  if (current && !isTokenExpiring(60)) {
    return current
  }

  const refreshToken = getRefreshToken()
  if (refreshToken) {
    const refreshed = await refreshAccessToken()
    if (refreshed) return refreshed
  }

  return current
}

export function redirectToLogin(returnTo = window.location.href): void {
  const appDomain = rootCookieDomain()
  const ssoDomain = (() => {
    try {
      const host = new URL(platformEnv.ssoFrontendUrl).hostname
      return host === 'hocnhe.com' || host.endsWith('.hocnhe.com') ? '.hocnhe.com' : undefined
    } catch {
      return undefined
    }
  })()
  const sameRootDomain = appDomain !== undefined && appDomain === ssoDomain
  const callback = new URL('/auth/callback', window.location.origin)
  callback.searchParams.set('returnTo', new URL(returnTo, window.location.origin).pathname)
  const target = sameRootDomain ? returnTo : callback.href
  const login = new URL('/auth/login', platformEnv.ssoFrontendUrl)
  login.searchParams.set('redirect_url', target)
  window.location.assign(login.href)
}

export async function exchangeAuthorizationCode(code: string): Promise<void> {
  const response = await axios.post(`${platformEnv.ssoApiUrl}/v1/auth/exchange-code`, { code }, { timeout: 10_000 })
  const payload = response.data?.data
  if (!response.data?.success || !payload?.access_token || !payload?.refresh_token || !payload?.expires_in) {
    throw new Error(response.data?.message || 'Không thể xác thực với SSO.')
  }
  saveTokens(payload)
}
